/* Sale Report Datewise — API integration.
 * Base: /pharmacy-ims/api/v1/reports  (httpSupplier already targets
 * REACT_APP_SUPPLIER_API_BASE_URL = …/pharmacy-ims/api/, so paths are v1/reports/…).
 *
 *   GET  v1/reports/sales?warehouseId=&status=&orderId=&medicineCode=&batchNo=
 *                         &paymentMode=&fromDate=&toDate=&search=&page=&size=
 *   POST v1/reports/sales/export  { …same filters }  -> a PENDING task
 *   GET  v1/reports/tasks/{taskId}                   -> poll until COMPLETED / FAILED
 *   GET  v1/reports/tasks/{taskId}/download          -> CSV blob
 *
 * The response envelope, the 45-day window rule and the export-task endpoints are
 * common to every item-level report — see reportApiShared.js.
 *
 * Ordering is fixed server-side (invoice date, newest first) — the report is an
 * item-level projection whose ORDER BY lives in the query, so no `sort` param. */
import { httpSupplier } from "../../HttpCommonALL";
import { normalizeTask, num, reportTasks, rethrow, unwrap, xUser } from "./reportApiShared";

const BASE = "v1/reports/sales";

/* Payment modes the backend accepts (PaymentMode enum). Parsing is
   case-insensitive server-side, but we send the canonical names. */
export const PAYMENT_MODES = [
  "CASH",
  "CARD",
  "UPI",
  "NET_BANKING",
  "WALLET",
  "CHEQUE",
  "CREDIT",
  "E_CLINIC_ONLINE",
  "B2C_ONLINE",
  "OTHER",
];

/* Build the shared filter object sent as query params (list) or JSON body
   (export). Blank values and the "ALL" sentinels are dropped so the backend
   treats them as unset. */
export const toQuery = (filters = {}) => {
  const q = {};
  if (filters.warehouseId) q.warehouseId = String(filters.warehouseId).trim();
  if (filters.status && filters.status !== "ALL") q.status = filters.status;
  if (filters.paymentMode && filters.paymentMode !== "ALL") q.paymentMode = filters.paymentMode;
  if (filters.orderId) q.orderId = filters.orderId.trim();
  if (filters.medicineCode) q.medicineCode = filters.medicineCode.trim();
  if (filters.batchNo) q.batchNo = filters.batchNo.trim();
  if (filters.fromDate) q.fromDate = filters.fromDate;
  if (filters.toDate) q.toDate = filters.toDate;
  if (filters.search) q.search = filters.search.trim();
  return q;
};

/* Map a SalesReportRow to the UI shape. The row is one invoice line item plus its
   invoice / customer / store context, the lot it was dispensed from and that
   lot's purchase origin (so a sale traces back to the GRN that stocked it).
   Everything arithmetic (tax split, MRP total, cost of sale, margin, discount
   roll-up) is already derived server-side — no money math here.

   Note the sale money model is the mirror of the purchase side: MRP is
   GST-inclusive, so `baseValue` is the amount with tax taken back OUT of it
   rather than a net cost with tax added on. */
const fromRow = (r = {}) => ({
  /* invoice header */
  invoiceId: r.invoiceId ?? null,
  invoiceNumber: r.invoiceNumber ?? "",
  invoiceDate: r.invoiceDate ?? "",
  createdAt: r.createdAt ?? "",
  status: (r.status ?? "").toString().toUpperCase(),
  orderId: r.orderId ?? "",
  transactionId: r.transactionId ?? "",
  paymentMode: r.paymentMode ?? "",
  customerName: r.customerName ?? "",
  customerPhone: r.customerPhone ?? "",
  doctorName: r.doctorName ?? "",
  remarks: r.remarks ?? "",
  createdBy: r.createdBy ?? "",
  /* store */
  warehouseId: r.warehouseId ?? "",
  warehouseName: r.warehouseName ?? "",
  warehouseCode: r.warehouseCode ?? "",
  state: r.state ?? "",
  city: r.city ?? "",
  address: r.address ?? "",
  pincode: r.pincode ?? "",
  /* line item */
  medicineCode: r.medicineCode ?? "",
  medicineName: r.medicineName ?? "",
  hsnCode: r.hsnCode ?? "",
  batchNo: r.batchNo ?? "",
  expiryDate: r.expiryDate ?? "",
  drugCategory: r.drugCategory ?? "",
  quantity: num(r.quantity),
  unitOfMeasure: r.unitOfMeasure ?? "",
  unitsPerStrip: num(r.unitsPerStrip),
  /* quantities normalised — strips for the money columns, loose units for the
     legacy extract's Quantity column */
  stripsSold: num(r.stripsSold),
  quantityUnits: num(r.quantityUnits),
  /* rates */
  sellingPrice: num(r.sellingPrice),
  mrp: num(r.mrp),
  ratePerStrip: num(r.ratePerStrip),
  /* discounts */
  discountPercentage: num(r.discountPercentage),
  productDiscountAmount: num(r.productDiscountAmount),
  cureCoinDiscount: num(r.cureCoinDiscount),
  totalDiscountAmount: num(r.totalDiscountAmount),
  totalDiscountPercentage: num(r.totalDiscountPercentage),
  /* tax / totals */
  gstPercentage: num(r.gstPercentage),
  taxDescription: r.taxDescription ?? "",
  mrpTotal: num(r.mrpTotal),
  amount: num(r.amount),
  baseValue: num(r.baseValue),
  taxAmount: num(r.taxAmount),
  cgstAmount: num(r.cgstAmount),
  sgstAmount: num(r.sgstAmount),
  igstAmount: num(r.igstAmount),
  utgstAmount: num(r.utgstAmount),
  /* lot dispensed from + its purchase origin */
  lotPurchasePrice: num(r.lotPurchasePrice),
  costOfSale: num(r.costOfSale),
  margin: num(r.margin),
  marginPercentage: num(r.marginPercentage),
  batchSupplierName: r.batchSupplierName ?? "",
  batchInvoiceNumber: r.batchInvoiceNumber ?? "",
  batchInvoiceDate: r.batchInvoiceDate ?? "",
  /* invoice totals, repeated on every line */
  invoiceTotalAmount: num(r.invoiceTotalAmount),
  invoiceDiscountAmount: num(r.invoiceDiscountAmount),
  invoiceCureCoinDiscount: num(r.invoiceCureCoinDiscount),
  invoiceGstAmount: num(r.invoiceGstAmount),
  invoiceNetAmount: num(r.invoiceNetAmount),
});

const salesReportService = {
  /* GET v1/reports/sales -> paged sale line items, newest invoice date first. */
  list(filters = {}, { page = 0, size = 20 } = {}) {
    return httpSupplier
      .get(BASE, { params: { ...toQuery(filters), page, size }, headers: xUser() })
      .then((r) => {
        const data = unwrap(r);
        const content = Array.isArray(data?.content) ? data.content : Array.isArray(data) ? data : [];
        return {
          content: content.map(fromRow),
          totalPages: data?.totalPages ?? (content.length ? 1 : 0),
          totalElements: data?.totalElements ?? content.length,
          page: data?.number ?? page,
          size: data?.size ?? size,
        };
      })
      .catch(rethrow);
  },

  /* POST v1/reports/sales/export -> start CSV generation (the legacy
     "Sales Report Template" column layout). Returns the normalized task so the
     caller can poll by taskId and name the download with the server-provided
     fileName. */
  requestExport(filters = {}) {
    return httpSupplier
      .post(`${BASE}/export`, toQuery(filters), { headers: xUser() })
      .then((r) => normalizeTask(unwrap(r)))
      .catch(rethrow);
  },

  /* Task polling / download are report-type-agnostic. */
  exportStatus: reportTasks.status,
  download: (taskId, filename = "sales-report.csv") => reportTasks.download(taskId, filename),
};

export default salesReportService;
